import { PALETTE } from "@eldrun/content";
import { Color, ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { ATMOSPHERE_UNIFORMS } from "./common.ts";

const vertexShader = /* glsl */ `
${ATMOSPHERE_UNIFORMS}
out vec3 vWorldPos;
out vec3 vColor;

void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorldPos = world.xyz;
  vColor = color;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const fragmentShader = /* glsl */ `
${ATMOSPHERE_UNIFORMS}
uniform vec3 uTint;
uniform float uBlocked;
in vec3 vWorldPos;
in vec3 vColor;

void main() {
  vec3 normal = normalize(cross(dFdx(vWorldPos), dFdy(vWorldPos)));
  vec3 view = normalize(cameraPosition - vWorldPos);
  float rim = 1.0 - abs(dot(normal, view));
  // Faixas que sobem devagar: lê como "projeção", não como objeto real.
  float bands = 0.5 + 0.5 * sin(vWorldPos.y * 18.0 - uTime * 4.0);
  // F75: vermelho é só do chefe. A posição válida acende na cor do slot (uTint); a inválida vira cinza
  // escuro com hachura cruzada (o ✕ do doc 09 §2.2 na superfície; o rótulo da mira traz o ✕ e o motivo).
  bool blocked = uBlocked > 0.5;
  vec3 tint = blocked ? vec3(0.08) : uTint;
  float k = (vWorldPos.x + vWorldPos.z) * 3.0;
  float hatch = blocked ? max(step(0.8, fract(k + vWorldPos.y * 3.0)), step(0.8, fract(k - vWorldPos.y * 3.0))) : 0.0;
  vec3 color = mix(vColor, tint, 0.65) + tint * rim * 0.6 - hatch * 0.25;
  float alpha = 0.32 + 0.18 * bands + 0.3 * rim + hatch * 0.2;
  gl_FragColor = vec4(color, alpha);
  #include <colorspace_fragment>
}
`;

/**
 * Fantasma de posicionamento: transparente, sem escrever profundidade; na cor do slot (`uTint`, o neutro
 * até a cena saber o slot) ou hachurado quando bloqueado (`uBlocked`).
 */
export function createGhostMaterial(atmosphere: AtmosphereUniforms): ShaderMaterial {
  return new ShaderMaterial({
    name: "fantasma",
    uniforms: { ...atmosphere, uTint: { value: new Color(PALETTE.neutro.hex) }, uBlocked: { value: 0 } },
    vertexShader,
    fragmentShader,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
  });
}
